import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';

export interface WhTab {
  /** Cible routerLink — absolue ('/devices/trackers'), relative ('./members') ou commands. */
  link: string | unknown[];
  label: string;
  /** routerLinkActiveOptions.exact — true pour un onglet « racine » qui ne doit pas rester allumé sur ses sous-routes sœurs. */
  exact?: boolean;
  /** Point rose à côté du libellé : il reste quelque chose à faire dans cet onglet. */
  dot?: boolean;
  /** Compteur à droite du libellé (variante segmented), ex. sessions de logs en cours. */
  count?: number | null;
  /**
   * Sous-écrans (variante underline) : l'onglet ouvre un menu au lieu de naviguer, et affiche
   * l'écran actif à côté de son libellé (« Positionnement · Bâtiment 3D »). `link` reste celui du
   * premier sous-écran.
   */
  children?: WhTab[];
  /** Mention discrète à droite d'un sous-écran du menu, ex. « admin ». */
  hint?: string;
}

/**
 * LA barre d'onglets de navigation. Deux rendus :
 *  - `underline` (défaut) : le pattern « -mb-px border-b-2 + souligné accentué » (détail site,
 *    sous-nav historiques).
 *  - `segmented` : groupe de pilules dans un cadre, l'onglet actif en aplat de couleur domaine.
 *    Utilisé dans `wh-section-header` (sections Devices/Gateways) pour que le titre de section ne
 *    soit plus dupliqué par l'onglet actif.
 *
 * L'accent porte la couleur du domaine : primary (rose, défaut), warmYellow (gateways),
 * aquamarine (users).
 */
@Component({
  selector: 'wh-tabs',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CdkOverlayOrigin, CdkConnectedOverlay],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (variant() === 'segmented') {
      <nav
        class="no-scrollbar inline-flex max-w-full gap-0.5 overflow-x-auto rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-slate-700 dark:bg-slate-800">
        @for (tab of tabs(); track tab.label) {
          <a
            [routerLink]="tab.link"
            routerLinkActive
            #rla="routerLinkActive"
            [routerLinkActiveOptions]="{ exact: tab.exact ?? false }"
            [attr.aria-label]="tab.dot ? tab.label + ' (étapes restantes)' : null"
            class="shrink-0 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100"
            [class]="rla.isActive ? segActiveClass() : ''">
            {{ tab.label }}
            @if (tab.count !== null && tab.count !== undefined) {
              <span
                data-testid="tab-count"
                class="ml-0.5 rounded px-1.5 text-xs font-bold"
                [class]="
                  rla.isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-200'
                "
                >{{ tab.count }}</span
              >
            }
            @if (tab.dot) {
              <span
                data-testid="tab-dot"
                class="bg-primary-500 ml-1.5 inline-block size-1.5 rounded-full align-middle"
                aria-hidden="true"></span>
            }
          </a>
        }
      </nav>
    } @else {
      <nav class="no-scrollbar flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-slate-700">
        @for (tab of tabs(); track tab.label; let i = $index) {
          @if (tab.children?.length) {
            <button
              type="button"
              cdkOverlayOrigin
              #origin="cdkOverlayOrigin"
              data-testid="tab-group"
              aria-haspopup="menu"
              [attr.aria-label]="groupLabel(tab)"
              [attr.aria-expanded]="openIndex() === i"
              class="-mb-px inline-flex shrink-0 items-center gap-1 whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100"
              [class]="isActive(tab) ? activeClass() : ''"
              (click)="toggle(i)">
              {{ tab.label }}
              @if (activeChild(tab); as child) {
                @if (child.label !== tab.label) {
                  <span class="font-normal opacity-60" aria-hidden="true">·</span>
                  <span>{{ child.label }}</span>
                }
              }
              <svg
                class="size-3.5 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true">
                <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              @if (tab.dot) {
                <span
                  data-testid="tab-dot"
                  class="bg-primary-500 ml-0.5 inline-block size-1.5 rounded-full"
                  aria-hidden="true"></span>
              }
            </button>
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="origin"
              [cdkConnectedOverlayOpen]="openIndex() === i"
              [cdkConnectedOverlayPositions]="menuPositions"
              (overlayOutsideClick)="close()"
              (overlayKeydown)="onMenuKey($event)"
              (detach)="close()">
              <div
                role="menu"
                [attr.aria-label]="tab.label"
                class="mt-1 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                @for (child of tab.children; track child.label) {
                  <a
                    role="menuitem"
                    [routerLink]="child.link"
                    (click)="close()"
                    class="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                    [class]="isActive(child) ? 'text-primary-500 font-bold' : 'text-gray-800 dark:text-slate-100'">
                    {{ child.label }}
                    @if (child.hint) {
                      <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400">{{ child.hint }}</span>
                    }
                  </a>
                }
              </div>
            </ng-template>
          } @else {
            <a
              [routerLink]="tab.link"
              routerLinkActive
              #rla="routerLinkActive"
              [routerLinkActiveOptions]="{ exact: tab.exact ?? false }"
              [attr.aria-label]="tab.dot ? tab.label + ' (étapes restantes)' : null"
              class="-mb-px shrink-0 whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100"
              [class]="rla.isActive ? activeClass() : ''">
              {{ tab.label }}
              @if (tab.dot) {
                <span
                  data-testid="tab-dot"
                  class="bg-primary-500 ml-1.5 inline-block size-1.5 rounded-full align-middle"
                  aria-hidden="true"></span>
              }
            </a>
          }
        }
      </nav>
    }
  `,
})
export class WhTabsComponent {
  readonly tabs = input.required<WhTab[]>();
  readonly accent = input<'primary' | 'warmYellow' | 'aquamarine'>('primary');
  readonly variant = input<'underline' | 'segmented'>('underline');

  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  /** URL courante : relue à chaque navigation pour savoir quel onglet à menu est actif. */
  readonly #url = toSignal(
    this.#router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.#router.url)
    ),
    { initialValue: this.#router.url }
  );
  /** Index de l'onglet dont le menu est ouvert, ou `null`. */
  protected readonly openIndex = signal<number | null>(null);
  protected readonly menuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  protected toggle(i: number): void {
    this.openIndex.update(open => (open === i ? null : i));
  }

  protected close(): void {
    this.openIndex.set(null);
  }

  protected onMenuKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /** Onglet (ou sous-écran) actif ; un onglet à menu l'est si l'un de ses sous-écrans l'est. */
  protected isActive(tab: WhTab): boolean {
    this.#url();
    if (tab.children?.length) {
      return tab.children.some(c => this.isActive(c));
    }
    const commands = Array.isArray(tab.link) ? tab.link : [tab.link];
    const tree = this.#router.createUrlTree(commands, { relativeTo: this.#route });
    return this.#router.isActive(tree, {
      paths: tab.exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  /** Libellé lu par les lecteurs d'écran : « Positionnement : Bâtiment 3D », ou le groupe seul. */
  protected groupLabel(tab: WhTab): string {
    const child = this.activeChild(tab);
    return child && child.label !== tab.label ? `${tab.label} : ${child.label}` : tab.label;
  }

  protected activeChild(tab: WhTab): WhTab | null {
    return tab.children?.find(c => this.isActive(c)) ?? null;
  }

  /**
   * Chaînes statiques complètes pour que le JIT Tailwind les voie.
   *
   * L'onglet actif porte la couleur du domaine sur le texte ET le soulignement (comme Trackers en
   * rose) : `-500` est une couleur vive lisible en clair comme en dark, donc pas d'invisibilité —
   * contrairement à l'ancien `!text-gray-900` (noir) qui disparaissait sur fond sombre.
   */
  protected readonly activeClass = computed(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return '!border-warmYellow-500 !font-bold !text-warmYellow-500';
      case 'aquamarine':
        return '!border-aquamarine-500 !font-bold !text-aquamarine-500';
      default:
        return '!border-primary-500 !font-bold !text-primary-500';
    }
  });

  /** Variante pilule : aplat de couleur domaine + texte blanc sur l'onglet actif. */
  protected readonly segActiveClass = computed(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return '!bg-warmYellow-500 !text-white shadow-sm';
      case 'aquamarine':
        return '!bg-aquamarine-500 !text-white shadow-sm';
      default:
        return '!bg-primary-500 !text-white shadow-sm';
    }
  });
}
